/**
 * Detection of All Access API downtime, scheduled or not.
 *
 * The API answers 503 with a maintenance payload during a scheduled window and the
 * platform answers 502/503/504 when the app itself is not running. Both mean no
 * route will serve, so the failure is reported once, from whichever request hit it,
 * and the app shows the maintenance screen in place of the routes.
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { MaintenanceReason } from "../Common/CommonTypes";

/** Body the API returns with a maintenance response. */
type MaintenancePayload = { maintenance?: boolean; message?: string; endsAt?: string };

type OnUnavailable = (reason: MaintenanceReason, message: string, endsAt: string | null) => void;

type Outage = { reason: MaintenanceReason; message: string; endsAt: string | null };

type RetriableConfig = InternalAxiosRequestConfig & { retriedAfterOutageCheck?: boolean };

/** Statuses that indicate the API as a whole is not serving; a 401, 404 or 500 is a single request failing. */
const UNAVAILABLE_STATUSES = [502, 503, 504];

const PROBE_TIMEOUT_MS = 5000;

const trimSlashes = (url: string) => url.replace(/\/+$/, "");

/** Requests to Arch Central and RIC share the same axios instance and are out of scope here. */
const ALL_ACCESS_API = trimSlashes((process.env.REACT_APP_API_ENDPOINT as string) ?? "");

export const isAllAccessApiError = (error: AxiosError): boolean =>
    ALL_ACCESS_API !== "" && trimSlashes(error.config?.baseURL ?? "") === ALL_ACCESS_API;

const reported = new WeakSet<object>();

/**
 * `axios.isCancel` is declared as `value is Cancel`, and `AxiosError` is assignable to
 * `Cancel`, so calling it inline narrows the argument to `never` in the negative branch
 * ("Property 'response' does not exist on type 'never'"). This wrapper keeps the
 * narrowing out of the caller.
 */
const isCanceled = (error: AxiosError): boolean => axios.isCancel(error);

/** Response bodies arrive as a `Blob` on `responseType: 'blob'` requests, where the payload cannot be read. */
const readPayload = (data: unknown): MaintenancePayload =>
    data !== null && typeof data === "object" ? (data as MaintenancePayload) : {};

const outage = (reason: MaintenanceReason, payload: MaintenancePayload = {}): Outage => ({
    reason,
    message: payload.message ?? "",
    endsAt: payload.endsAt ?? null,
});

/**
 * Classifies a request failure.
 *
 * @returns why the API is unavailable, or `null` when the failure is request-level
 * and the caller's own error handling applies.
 */
const classifyResponse = (status: number, data: unknown): Outage | null => {
    if (!UNAVAILABLE_STATUSES.includes(status)) return null;

    // Only the API sends the maintenance payload. A status without it comes from the
    // platform - a stopped app, a failed deploy - and is not a scheduled window.
    const payload = readPayload(data);
    return payload.maintenance === true ? outage("maintenance", payload) : outage("unavailable");
};

const probe = async (url: string, method: "GET" | "HEAD" = "GET"): Promise<Response | null> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

    try {
        return await fetch(url, { method, cache: "no-store", signal: controller.signal });
    } catch {
        return null;
    } finally {
        clearTimeout(timer);
    }
};

const diagnoseNoAnswer = async (): Promise<Outage | null> => {
    if (navigator.onLine === false) return outage("offline");

    const status = await probe(`${ALL_ACCESS_API}/api/Maintenance/Status`);

    if (status !== null) {
        if (!status.ok) return classifyResponse(status.status, null);

        const payload = readPayload(await status.json().catch(() => null));
        return payload.maintenance === true ? outage("maintenance", payload) : null;
    }

    const site = await probe(`${window.location.origin}/`, "HEAD");
    return site === null ? outage("offline") : outage("unavailable");
};

let diagnosis: Promise<Outage | null> | null = null;

const sharedDiagnosis = (): Promise<Outage | null> => {
    if (diagnosis === null) {
        diagnosis = diagnoseNoAnswer().finally(() => {
            diagnosis = null;
        });
    }

    return diagnosis;
};

/**
 * Whether a request failed because the API is unavailable.
 *
 * @param error value caught from an axios call.
 * @returns `true` when the maintenance screen already accounts for this failure and
 * the caller should not report it again.
 */
export const isApiUnavailableError = (error: unknown): boolean =>
    typeof error === "object" && error !== null && reported.has(error);

/**
 * Registers a response interceptor on the default axios instance that reports API
 * unavailability to `onUnavailable`.
 *
 * The check has to run in an interceptor: the `try*` helpers in the API clients catch
 * their own errors and resolve to `undefined`, so the failure never reaches application
 * code. Rejections are re-thrown unchanged, leaving existing error handling in place.
 *
 * @param onUnavailable receives the reason and, for a scheduled window, the message and
 * end time supplied by the API.
 */
export const installMaintenanceInterceptor = (onUnavailable: OnUnavailable): void => {
    axios.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
            if (!isAllAccessApiError(error) || isCanceled(error)) return Promise.reject(error);

            const found = error.response
                ? classifyResponse(error.response.status, error.response.data)
                : await sharedDiagnosis();

            if (found !== null) {
                reported.add(error);
                onUnavailable(found.reason, found.message, found.endsAt);
                return Promise.reject(error);
            }

            const config = error.config as RetriableConfig | undefined;
            const isLostRead = !error.response && config?.method?.toLowerCase() === "get" && !config.retriedAfterOutageCheck;

            if (config && isLostRead) {
                config.retriedAfterOutageCheck = true;
                return axios(config);
            }

            return Promise.reject(error);
        }
    );
};
